import { useRef, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCart } from "../../hooks/useCart";
import { useCurrentSession } from "../../hooks/useSession";
import Button from "../../components/ui/Button";
import ConfirmationModal from "../../components/ui/ConfirmationModal";
import InvoiceCartPrintContent from "./components/InvoiceCartPrintContent";
import { MdQrCode } from "react-icons/md";
import { useConfirmationModal } from "../../hooks/useConfirmationModal";
import PopupModal from "../../components/ui/PopupModal";
import CartQRPopup from "@/components/ui/CartQRPopup";
import CartScroll from "./CartScroll";
import PhoneInput from "@/components/ui/PhoneInput";
import { useCreateInvoice } from "@/hooks/useInvoices";
import { toast } from "react-toastify";
import { generateCryptoUniqueOrderId } from "@/utils/uniqueId";

import { getCartFormSchema } from "../../validations/cartSchema";
import CartSummary from "./components/CartSummary";
import { useCouponManager } from "../../hooks/useCouponManager";
import { useCartLogic } from "../../hooks/useCartLogic";
import { Coupon } from "../../hooks/useCoupons";
import { getApiInstance } from "@/utils/api";

const CartPage = () => {
  const navigate = useNavigate();

  // فحص حالة الجلسة
  const { data: sessionData, isLoading: isLoadingSession } =
    useCurrentSession();

  // فحص إذا كانت الجلسة منتهية أو غير موجودة → توجيه إلى صفحة بدء الجلسة بدون تسجيل خروج تلقائي
  useEffect(() => {
    const sessionStarted = localStorage.getItem("sessionStarted");
    // إذا انتهى التحميل ولم توجد جلسة نشطة
    if (!isLoadingSession && !sessionData && sessionStarted) {
      toast.error("لا توجد جلسة نشطة، ابدأ جلسة جديدة");
      navigate("/start-session");
      return;
    }

    // إذا كانت الجلسة موجودة لكنها منتهية
    if (sessionData && sessionData.ended_at && sessionStarted) {
      toast.error("انتهت الجلسة، ابدأ جلسة جديدة");
      navigate("/start-session");
      return;
    }
  }, [sessionData, isLoadingSession, navigate]);

  const {
    cart,
    clearCart,
    coupon,
    qrKey,
    applyCoupon,
    clearCoupon,
    clearQRKey,
    orderData,
  } = useCart();

  const { mutate: createInvoice } = useCreateInvoice();
  const api = getApiInstance();
  const printRef = useRef<HTMLDivElement>(null);

  const [qrModal, setQrModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const [isPaying, setIsPaying] = useState(false);
  const [pendingPaymentData, setPendingPaymentData] = useState<any>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  // حارس رسائل WebView لتفادي سباق التوقيت
  const lastPaymentMsgRef = useRef<{ status?: "success" | "fail" } | null>(
    null
  );
  // مرجع لـ timeout معالجة الدفعات المعلقة
  const paymentTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // حماية من الرسائل المكررة من WebView
  const processedMessages = useRef(new Set<string>());
  // حماية من print duplicates
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const mobileRegex =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      setIsMobile(mobileRegex.test(userAgent) || window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const totalQuantity = cart.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = cart.reduce(
    (sum, item) => sum + Number(item.price) * item.quantity,
    0
  );

  // إعادة التوجيه تلقائياً للصفحة الرئيسية إذا كانت السلة فارغة
  useEffect(() => {
    if (cart.length === 0) {
      navigate("/");
    }
  }, [cart.length, navigate]);

  const discountAmount = useMemo(() => {
    if (coupon) return (coupon.discount_percentage / 100) * totalPrice;
    if (qrKey) return qrKey.data.points_spent || 0;
    return 0;
  }, [coupon, qrKey, totalPrice]);

  const finalPrice = Math.max(totalPrice - discountAmount, 0);
  const finalDue = Number(finalPrice.toFixed(2));
  const hasSubscription = cart.some((item) => item.type === "subscription");
  const isFromOrder = Boolean(orderData);

  const formSchema = useMemo(
    () => getCartFormSchema(finalDue, hasSubscription, isFromOrder),
    [finalDue, hasSubscription, isFromOrder]
  );

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    setFocus,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      phone: orderData?.phone || "",
      discountCode: orderData?.coupon || coupon?.code || "",
      cashAmount: "0.00",
      cardAmount: finalDue.toFixed(2),
    },
  });

  useEffect(() => {
    if (orderData) {
      setValue("phone", orderData.phone);
      if (orderData.coupon) {
        setValue("discountCode", orderData.coupon);
      }
    }
  }, [orderData, setValue]);

  const watchedCode = watch("discountCode")?.trim();
  const watchedCashStr = (watch("cashAmount") as unknown as string) ?? "0";
  const watchedCardStr = (watch("cardAmount") as unknown as string) ?? "0";
  const watchedCash = Number(watchedCashStr) || 0;
  const watchedCard = Number(watchedCardStr) || 0;
  const totalPaid = watchedCash + watchedCard;
  const isPaymentSufficient = totalPaid >= finalDue;

  const {
    popup,
    setPopup,
    handleVerify: handleVerifyCoupon,
    couponLoading,
  } = useCouponManager(watchedCode, applyCoupon);

  const {
    show: showCancelModal,
    open: openCancelModal,
    close: closeCancelModal,
  } = useConfirmationModal();
  const {
    show: showOrderModal,
    open: openOrderModal,
    close: closeOrderModal,
  } = useConfirmationModal();

  const {
    onSubmit,

    generatedInvoice,
    printInvoice,
    setGeneratedInvoice,
    setPrintInvoice,
    isCreatingInvoice,
    isGiftPrint,
    giftInvoiceData,
    precheckInvoiceBeforeCardPayment,
  } = useCartLogic({
    cart,
    coupon,
    finalPrice,
    api,
    createInvoice,
    clearCart,
    closeConfirmModal: closeOrderModal,
    printRef,
    pendingPaymentData,
    showErrorPopup: (message: string) =>
      setPopup({ type: "error", message, icon: "/icons/error.svg" }),
  });

  useEffect(() => {
    if (generatedInvoice && !isPrinting) {
      // التحقق من صحة بيانات الفاتورة قبل الطباعة
      if (
        !generatedInvoice.id ||
        !generatedInvoice.items ||
        generatedInvoice.items.length === 0
      ) {
        console.error("بيانات الفاتورة غير صحيحة:", generatedInvoice);
        setPopup({
          type: "error",
          message: "فشل في جلب بيانات الطباعة - بيانات الفاتورة غير مكتملة",
          icon: "/icons/error.svg",
        });
        setGeneratedInvoice(null);
        setPrintInvoice(null);
        return;
      }

      // حماية من print duplicates
      setIsPrinting(true);

      // طباعة الفاتورة مباشرة بدون نافذة التأكيد
      if (printRef.current) {
        const html = printRef.current.innerHTML;

        // التحقق من أن HTML يحتوي على محتوى صحيح
        if (!html || html.trim().length < 100) {
          console.error("محتوى HTML للطباعة فارغ أو غير صحيح:", html);
          setPopup({
            type: "error",
            message: "فشل في جلب بيانات الطباعة - محتوى الفاتورة فارغ",
            icon: "/icons/error.svg",
          });
          setGeneratedInvoice(null);
          setPrintInvoice(null);
          return;
        }

        const payload = {
          action: "printInvoice",
          html,
        };

        if (window.ReactNativeWebView?.postMessage) {
          window.ReactNativeWebView.postMessage(JSON.stringify(payload));
        } else {
          // precise browser print fallback
          const printWindow = window.open("", "_blank", "width=400,height=600");
          if (printWindow) {
            printWindow.document.open();
            printWindow.document.write(`<!DOCTYPE html>
              <html dir="rtl" lang="ar">
                <head>
                  <meta charset="UTF-8" />
                  <title>Print</title>
                  <style>
                    @page { size: 58mm auto; margin: 0; }
                    html, body { margin: 0; padding: 0; background: #fff; }
                    /* تكبير أحجام الخطوط للطباعة */
                    * { font-size: 14px !important; }
                    .items-table th { font-size: 10px !important; font-weight: 600; }
                    .items-table td { font-size: 14px !important; }
                    .header .title { font-size: 20px !important; font-weight: bold; }
                  </style>
                </head>
                <body>${html}</body>
              </html>`);
            printWindow.document.close();
            printWindow.focus();
            printWindow.onload = () => {
              printWindow.print();
              setTimeout(() => printWindow.close(), 100);
            };
          } else {
            window.print();
          }
        }

        // بعد الطباعة: نظّف السلة وأعد التوجيه للصفحة الرئيسية
        clearCart();
        setGeneratedInvoice(null);
        setPrintInvoice(null);
        setIsPrinting(false);
        navigate("/");
      }
    }
  }, [generatedInvoice, isPrinting, clearCart, navigate]);

  const handleRemoveDiscount = () => {
    if (coupon) {
      clearCoupon();
      setPopup({
        type: "success",
        message: "تم إزالة كود الخصم بنجاح",
        icon: "/icons/success.svg",
      });
    }
    if (qrKey) {
      clearQRKey();
      setPopup({
        type: "success",
        message: "تم إزالة QR Key بنجاح",
        icon: "/icons/success.svg",
      });
    }
  };

  // منطق استقبال رسالة الدفع من Nearpay
  useEffect(() => {
    const handler = (event: any) => {
      let rawData = event?.data || event?.detail;
      if (typeof rawData === "string") {
        try {
          rawData = JSON.parse(rawData);
        } catch {}
      }

      const data = rawData?.data || rawData?.detail || rawData;

      // حماية من الرسائل المكررة
      const messageId = JSON.stringify(data);
      if (processedMessages.current.has(messageId)) {
        if (process.env.NODE_ENV === "development") {
          console.log("⚠️ Duplicate message ignored:", data);
        }
        return;
      }
      processedMessages.current.add(messageId);

      console.log("📦 Unified payment handler:", data);

      if (data?.status === "success") {
        setIsPaying(false);
        // تنظيف timeout عند نجاح الدفع
        if (paymentTimeoutRef.current) {
          clearTimeout(paymentTimeoutRef.current);
          paymentTimeoutRef.current = null;
        }

        if (pendingPaymentData) {
          if (process.env.NODE_ENV === "development") {
            console.log(
              "✅ Payment success - executing onSubmit with pendingPaymentData"
            );
          }
          // تصفير البيانات المعلقة قبل استدعاء onSubmit لمنع التكرار
          const paymentDataToProcess = pendingPaymentData;
          setPendingPaymentData(null);
          lastPaymentMsgRef.current = null;

          onSubmit(paymentDataToProcess);
        } else {
          // خزّن الرسالة مؤقتاً إلى أن تتوفر بيانات الدفع
          if (process.env.NODE_ENV === "development") {
            console.log(
              "⏳ Payment success - storing message for later execution"
            );
          }
          lastPaymentMsgRef.current = { status: "success" };
        }
      } else if (data?.status === "fail" || data?.status === "cancelled") {
        // معالجة فشل أو إلغاء الدفع
        if (process.env.NODE_ENV === "development") {
          console.log("🚫 Payment cancelled/failed, cleaning up state");
        }
        setIsPaying(false);
        setPendingPaymentData(null);
        lastPaymentMsgRef.current = null; // تصفير المرجع لتجنب التأثير على الطلبات التالية

        // تنظيف timeout عند فشل أو إلغاء الدفع
        if (paymentTimeoutRef.current) {
          clearTimeout(paymentTimeoutRef.current);
          paymentTimeoutRef.current = null;
        }

        // إظهار رسالة مناسبة للمستخدم
        const isCancelled = data?.status === "cancelled";
        toast.error(
          isCancelled
            ? "تم إلغاء عملية الدفع، يمكنك المحاولة مرة أخرى"
            : "فشلت عملية الدفع، يرجى المحاولة مرة أخرى"
        );
      }
    };

    window.addEventListener("message", handler);
    window.addEventListener("paymentResult", handler as EventListener);
    return () => {
      window.removeEventListener("message", handler);
      window.removeEventListener("paymentResult", handler as EventListener);
      // تنظيف timeout عند unmount المكون
      if (paymentTimeoutRef.current) {
        clearTimeout(paymentTimeoutRef.current);
        paymentTimeoutRef.current = null;
      }
      // تنظيف الرسائل المعالجة
      processedMessages.current.clear();
    };
  }, [onSubmit]);

  // مراقبة تغييرات pendingPaymentData للتشخيص (يمكن إزالة هذا في الإنتاج)
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      console.log("📊 pendingPaymentData changed:", pendingPaymentData);
    }
  }, [pendingPaymentData]);

  // إذا وصلت الرسالة بالنجاح قبل تجهيز بيانات الدفع، ننفذ onSubmit عند توفرها
  useEffect(() => {
    if (pendingPaymentData && lastPaymentMsgRef.current?.status === "success") {
      if (process.env.NODE_ENV === "development") {
        console.log("🔄 Executing onSubmit from pendingPaymentData");
      }
      // تصفير البيانات المعلقة قبل استدعاء onSubmit لمنع التكرار
      const paymentDataToProcess = pendingPaymentData;
      setPendingPaymentData(null);
      lastPaymentMsgRef.current = null;

      onSubmit(paymentDataToProcess);
    }
  }, [pendingPaymentData, onSubmit]);

  // تنظيف شامل عند unmount المكون
  useEffect(() => {
    return () => {
      // تنظيف timeout
      if (paymentTimeoutRef.current) {
        clearTimeout(paymentTimeoutRef.current);
        paymentTimeoutRef.current = null;
      }
      // تنظيف state
      setIsPaying(false);
      setPendingPaymentData(null);
      setIsProcessing(false);
      setIsPrinting(false);
      lastPaymentMsgRef.current = null;
      // تنظيف الرسائل المعالجة
      processedMessages.current.clear();
    };
  }, []);

  // منطق تأكيد الطلب مع الدفع عبر الشبكة
  const handleConfirmOrder = handleSubmit(
    async (data) => {
      // حماية من double-click والمعالجة المكررة
      if (isProcessing) {
        if (process.env.NODE_ENV === "development") {
          console.log("⚠️ handleConfirmOrder blocked - already processing");
        }
        return;
      }

      setIsProcessing(true);

      try {
        const cardAmountNum = Number((data as any).cardAmount) || 0;
        const cashAmountNum = Number((data as any).cashAmount) || 0;
        const normalizedData = {
          ...data,
          cardAmount: cardAmountNum,
          cashAmount: cashAmountNum,
        } as any;
        if (cardAmountNum > 0) {
          // إلغاء أي عملية دفع جارية قبل بدء عملية جديدة
          if (process.env.NODE_ENV === "development") {
            console.log(
              "💳 Checking payment state - isPaying:",
              isPaying,
              "pendingPaymentData:",
              pendingPaymentData
            );
          }
          if (isPaying || pendingPaymentData) {
            if (process.env.NODE_ENV === "development") {
              console.log(
                "🔄 Cancelling previous payment and starting new one"
              );
            }

            // إلغاء العملية السابقة
            setIsPaying(false);
            setPendingPaymentData(null);
            lastPaymentMsgRef.current = null;

            // تنظيف timeout السابق
            if (paymentTimeoutRef.current) {
              clearTimeout(paymentTimeoutRef.current);
              paymentTimeoutRef.current = null;
            }

            // إرسال أمر إلغاء للـ WebView (إذا كان متاحاً)
            if (window.ReactNativeWebView?.postMessage) {
              window.ReactNativeWebView.postMessage(
                JSON.stringify({
                  action: "cancelPayment",
                })
              );
            }

            toast.info("تم إلغاء العملية السابقة وبدء عملية دفع جديدة");

            // انتظار قصير لضمان معالجة الإلغاء في WebView
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
          if (process.env.NODE_ENV === "development") {
            console.log("✅ Starting new payment process");
          }

          // قبل إرسال أمر الدفع للـ WebView: نتحقق من صحة الفاتورة عبر الباك اند
          const uniqueOrderId = `ORDER_${generateCryptoUniqueOrderId()}`;
          const precheck = await precheckInvoiceBeforeCardPayment(
            normalizedData,
            uniqueOrderId
          );
          if (!precheck.ok) {
            setIsPaying(false);
            return;
          }

          // فقط إذا التحقق نجح، نبدأ الدفع الشبكي
          setIsPaying(true);
          const paymentDataWithVisaOrderId = {
            ...normalizedData,
            visa_order_id: precheck.orderId,
          };

          if (process.env.NODE_ENV === "development") {
            console.log(
              "🔄 Setting pending payment data:",
              paymentDataWithVisaOrderId
            );
          }
          setPendingPaymentData(paymentDataWithVisaOrderId);

          // إعداد timeout للتعامل مع حالات عدم الرد من WebView (مثل إغلاق التطبيق)
          if (paymentTimeoutRef.current) {
            clearTimeout(paymentTimeoutRef.current);
          }
          paymentTimeoutRef.current = setTimeout(() => {
            // استخدام المراجع للتحقق من الحالة الحالية (تجنب stale closure)
            setPendingPaymentData((currentPending: any) => {
              if (currentPending) {
                // في حالة انتهاء المهلة، نظف الحالة وأبلغ المستخدم
                setIsPaying(false);
                lastPaymentMsgRef.current = null;
                toast.error("انتهت مهلة عملية الدفع، يرجى المحاولة مرة أخرى");
                console.warn(
                  "Payment timeout: no response from WebView after 1 minute"
                );
                return null; // تصفير pendingPaymentData
              }
              return currentPending; // لا تغيير إذا لم تكن هناك بيانات معلقة
            });
          }, 1 * 60 * 1000); // دقيقه timeout

          const payload = {
            action: "startPayment",
            amount: cardAmountNum * 100,
            orderId: precheck.orderId,
          };

          if (process.env.NODE_ENV === "development") {
            console.log("📤 Sending payment request to WebView:", payload);
          }

          window.ReactNativeWebView?.postMessage(JSON.stringify(payload));
        } else {
          onSubmit(normalizedData);
        }
        closeOrderModal();
      } finally {
        setIsProcessing(false);
      }
    },
    (formErrors) => {
      closeOrderModal();
      if (formErrors.phone) {
        setFocus("phone");
      }
    }
  );

  // تحديث قيمة cardAmount تلقائياً عند تغيّر المبلغ النقدي أو السعر النهائي
  useEffect(() => {
    const autoCard = Math.max(finalDue - watchedCash, 0);
    setValue("cardAmount", autoCard.toFixed(2), {
      shouldValidate: true,
    });
  }, [finalDue, watchedCash, setValue]);

  // Sanitization لحقل النقدي: السماح بأرقام ونقطة واحدة وتقريب عند الخروج من الحقل
  const handleCashChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value ?? "";
    const onlyNums = raw.replace(/[^\d.]/g, "");
    const singleDot = onlyNums.replace(/\.(?=.*\.)/g, "");
    e.currentTarget.value = singleDot;
    setValue("cashAmount", singleDot, { shouldValidate: true });
  };

  const handleCashBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const sanitized = String(e.target.value ?? "")
      .replace(/[^\d.]/g, "")
      .replace(/\.(?=.*\.)/g, "");
    const isValidNumeric = /^\d+(\.\d+)?$/.test(sanitized);
    if (!isValidNumeric) {
      e.currentTarget.value = "0.00";
      setValue("cashAmount", "0.00", { shouldValidate: true });
      return;
    }
    const n = Number(sanitized);
    const formatted = Number.isNaN(n) ? "0.00" : n.toFixed(2);
    e.currentTarget.value = formatted;
    setValue("cashAmount", formatted, { shouldValidate: true });
  };

  return (
    <>
      {isCreatingInvoice && (
        <div
          className='fixed inset-0 bg-black/40 z-50 flex items-center justify-center text-white text-xl'
          dir='rtl'
        >
          <div className='bg-black/70 px-6 py-4 rounded-xl shadow-lg'>
            برجاء الانتظار حتي تتم إنشاء الفاتورة...
          </div>
        </div>
      )}
      <CartQRPopup
        open={qrModal}
        onClose={() => setQrModal(false)}
        onResult={(matched: Coupon | null, code: string) => {
          setQrModal(false);
          if (matched) {
            applyCoupon(matched);
            setPopup({
              type: "success",
              message: `تم تطبيق بنجاح! خصم ${matched.discount_percentage}%`,
              icon: "/icons/success.svg",
            });
          } else {
            setPopup({
              type: "error",
              message: `الكود "${code}" من QR غير صالح أو منتهي الصلاحية`,
              icon: "/icons/error.svg",
            });
          }
        }}
      />

      {(printInvoice || isGiftPrint) && (
        <div
          style={{
            visibility: "hidden",
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: -1,
          }}
        >
          <div ref={printRef}>
            <InvoiceCartPrintContent
              invoice={printInvoice || undefined}
              isGift={isGiftPrint}
              giftData={giftInvoiceData}
            />
          </div>
        </div>
      )}

      <form
        onSubmit={handleConfirmOrder}
        className={`${
          isMobile ? "p-3 space-y-4" : "p-4 space-y-6"
        } max-w-4xl mx-auto print:hidden`}
      >
        <h2
          className={`${
            isMobile ? "text-xl" : "text-2xl"
          } font-bold text-right`}
        >
          السلة
        </h2>

        <CartScroll cart={cart} />

        {/* Mobile Number */}
        <PhoneInput
          control={control}
          name='phone'
          error={errors.phone?.message}
          label={
            isFromOrder
              ? `رقم الجوال - ${orderData?.name}`
              : hasSubscription || finalDue === 0
              ? "رقم الجوال (مطلوب للاشتراكات/الهدايا)"
              : "رقم الجوال (اختياري)"
          }
          defaultValue={orderData?.phone || ""}
          disabled={isFromOrder}
        />

        {/* عرض معلومات QR Key إذا كان موجود */}
        {qrKey && (
          <div className='bg-green-50 border-2 border-green-200 rounded-lg p-4 text-right'>
            <div className='flex items-center justify-between'>
              <div>
                <h3 className='font-bold text-green-800 text-lg'>
                  🔑 QR Key مُفعّل
                </h3>
                <p className='text-green-700 mt-1'>
                  <strong>الخدمة:</strong> {qrKey.data.service_info.name}
                </p>
                <p className='text-green-700'>
                  <strong>الحجم:</strong> {qrKey.data.service_size_info.size}
                </p>
                {qrKey.data.points_spent > 0 && (
                  <p className='text-green-700'>
                    <strong>النقاط المستخدمة:</strong> {qrKey.data.points_spent}
                  </p>
                )}
                <p className='text-sm text-green-600 mt-2'>
                  <strong>مفتاح:</strong> {qrKey.key}
                </p>
              </div>
              {!isFromOrder && (
                <button
                  type='button'
                  onClick={handleRemoveDiscount}
                  className='bg-red-500 text-white px-3 py-1 rounded-lg hover:bg-red-600 transition text-sm'
                >
                  إزالة
                </button>
              )}
            </div>
          </div>
        )}

        {/* كود الخصم - محسن للموبايل */}
        <div
          className={`${
            isMobile ? "space-y-3" : "flex items-center gap-4"
          } text-right`}
        >
          <input
            type='text'
            placeholder={isMobile ? "كود الخصم" : "ادخل كود الخصم"}
            {...register("discountCode")}
            disabled={!!coupon || !!qrKey || isFromOrder}
            className={`${
              isMobile ? "w-full" : "flex-1"
            } border border-gray-300 rounded-lg px-4 py-3 ${
              isMobile ? "text-base" : "text-lg"
            } focus:ring-2 focus:ring-red-500 ${
              coupon || qrKey || isFromOrder
                ? "bg-gray-100 cursor-not-allowed text-gray-500"
                : ""
            }`}
          />

          <div className={`${isMobile ? "flex gap-2" : "flex gap-4"}`}>
            {coupon || qrKey || isFromOrder ? (
              <button
                type='button'
                onClick={handleRemoveDiscount}
                disabled={isFromOrder}
                className={`${
                  isMobile ? "flex-1 px-3 py-3 text-sm" : "px-4 py-2"
                } rounded-lg font-bold transition ${
                  isFromOrder
                    ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                    : "bg-red-600 text-white hover:bg-red-700"
                }`}
              >
                {isFromOrder
                  ? "كوبون من الطلب"
                  : qrKey
                  ? "إزالة QR Key"
                  : "إزالة كود الخصم"}
              </button>
            ) : (
              <button
                type='button'
                onClick={handleVerifyCoupon}
                disabled={
                  !watch("discountCode") || !!coupon || !!qrKey || couponLoading
                }
                className={`${
                  isMobile ? "flex-1 px-3 py-3 text-sm" : "px-4 py-2"
                } rounded-lg font-bold transition ${
                  !watch("discountCode") || !!coupon || !!qrKey || couponLoading
                    ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                    : "bg-red-600 text-white hover:bg-red-700"
                }`}
              >
                {couponLoading ? "جاري البحث..." : "تحقق"}
              </button>
            )}
            <button
              type='button'
              className={`${
                isMobile ? "px-4 py-3" : "flex flex-col items-center gap-1 px-2"
              } text-red-700 font-bold ${
                !!coupon || !!qrKey || isFromOrder
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              } ${isMobile ? "rounded-lg border border-red-700" : ""}`}
              onClick={() =>
                !isFromOrder && !coupon && !qrKey && setQrModal(true)
              }
              disabled={!!coupon || !!qrKey || isFromOrder}
            >
              {isMobile ? (
                <div className='flex items-center gap-2'>
                  <MdQrCode className='w-5 h-5' />
                  <span className='text-sm'>مسح QR</span>
                </div>
              ) : (
                <>
                  <MdQrCode className='w-6 h-6' />
                  مسح QR
                </>
              )}
            </button>
          </div>
        </div>

        <div className='flex flex-col text-right space-y-2'>
          <label className='text-gray-700 font-medium'>مبلغ المستحق</label>
          <input
            type='text'
            readOnly
            value={finalPrice.toFixed(2)}
            className='border border-gray-300 rounded-lg px-4 py-3 text-lg bg-gray-100'
          />
        </div>

        {/* المبالغ النقدية وعبر الشبكة - محسن للموبايل */}
        <div className={`${isMobile ? "space-y-4" : "flex gap-2"} w-full`}>
          {["cashAmount", "cardAmount"].map((field) => (
            <div
              key={field}
              className={`flex flex-col gap-2 ${
                isMobile ? "w-full" : "flex-1"
              }`}
            >
              <label
                className={`text-gray-700 font-medium ${
                  isMobile ? "text-base" : ""
                }`}
              >
                {field === "cashAmount"
                  ? isMobile
                    ? "مبلغ نقدي"
                    : "كم مبلغ نقديًا"
                  : isMobile
                  ? "مبلغ شبكة (تلقائي)"
                  : "كم مبلغ شبكة (تلقائي)"}
              </label>
              <div className='relative w-full'>
                <input
                  type='text'
                  inputMode='decimal'
                  placeholder={
                    field === "cashAmount" ? "مبلغ نقدي" : "مبلغ شبكة"
                  }
                  {...register(field as "cashAmount" | "cardAmount")}
                  onChange={
                    field === "cashAmount" ? handleCashChange : undefined
                  }
                  onBlur={field === "cashAmount" ? handleCashBlur : undefined}
                  readOnly={field === "cardAmount"}
                  className={`w-full border border-gray-300 rounded-lg px-4 py-3 ${
                    isMobile ? "text-base" : "text-lg"
                  } focus:ring-2 focus:ring-red-500 ${
                    field === "cardAmount"
                      ? "bg-gray-100 cursor-not-allowed text-gray-700"
                      : "pl-24 pr-4"
                  }`}
                />
                {field === "cashAmount" && (
                  <button
                    type='button'
                    onClick={() =>
                      setValue("cashAmount", finalDue.toFixed(2), {
                        shouldValidate: true,
                      })
                    }
                    className='absolute left-0 inset-y-0 my-auto bg-red-600 hover:bg-red-700 text-white text-xs px-3 h-full rounded'
                  >
                    الكل نقدى
                  </button>
                )}
              </div>
              {errors[field as "cashAmount" | "cardAmount"] && (
                <span
                  className={`text-red-600 ${isMobile ? "text-xs" : "text-sm"}`}
                >
                  {errors[field as "cashAmount" | "cardAmount"]?.message}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* ملخص الأسعار - محسن للموبايل */}
        <CartSummary
          totalQuantity={totalQuantity}
          totalPrice={totalPrice}
          discountAmount={discountAmount}
          finalPrice={finalPrice}
          coupon={coupon}
          isMobile={isMobile}
        />

        {/* أزرار التحكم - محسن للموبايل */}
        <div className={`${isMobile ? "space-y-3" : "flex gap-2"}`}>
          {finalDue > 0 ? (
            <>
              <Button
                type='primary'
                onClick={openOrderModal}
                disabled={
                  cart.length === 0 || !isPaymentSufficient || isProcessing
                }
                className={isMobile ? "w-full py-4 text-lg" : ""}
              >
                {isPaying || pendingPaymentData
                  ? "إعادة المحاولة"
                  : isMobile
                  ? `تأكيد الطلب (${totalQuantity} صنف - ${finalPrice.toFixed(
                      2
                    )} ريال)`
                  : `تأكيد الطلب (${totalQuantity} صنف ${finalPrice.toFixed(
                      2
                    )} ريال)`}
              </Button>
              <Button
                type='secondary'
                onClick={openCancelModal}
                className={isMobile ? "w-full py-4 text-lg" : ""}
              >
                إلغاء
              </Button>
            </>
          ) : (
            <>
              <Button
                type='primary'
                onClick={openOrderModal}
                disabled={cart.length === 0 || isProcessing}
                className={isMobile ? "w-full py-4 text-lg" : ""}
              >
                {isPaying || pendingPaymentData
                  ? "إعادة المحاولة"
                  : "اطلب الهدية"}
              </Button>
              <Button
                type='secondary'
                onClick={openCancelModal}
                className={isMobile ? "w-full py-4 text-lg" : ""}
              >
                إلغاء
              </Button>
            </>
          )}
        </div>
      </form>

      <ConfirmationModal
        show={showCancelModal}
        title='تأكيد الإلغاء'
        message='هل تريد إلغاء الطلب وإفراغ السلة؟'
        confirmText='نعم'
        cancelText='لا'
        onConfirm={() => {
          // تنظيف أي timeout معلق عند إلغاء الطلب
          if (paymentTimeoutRef.current) {
            clearTimeout(paymentTimeoutRef.current);
            paymentTimeoutRef.current = null;
          }
          // تنظيف بيانات الدفع المعلقة
          setPendingPaymentData(null);
          lastPaymentMsgRef.current = null;
          setIsPaying(false);

          clearCart();
          setGeneratedInvoice(null);
          setPrintInvoice(null);
          closeCancelModal();
          navigate("/");
        }}
        onCancel={() => {
          closeCancelModal();
          toast.success("تم إلغاء الطلب بنجاح");
        }}
      />

      <ConfirmationModal
        show={showOrderModal}
        title='تأكيد الطلب'
        message={`هل تريد تأكيد الطلب وطباعة الفاتورة بقيمة ${finalPrice.toFixed(
          2
        )} ريال؟`}
        confirmText='تأكيد'
        cancelText='إلغاء'
        onConfirm={handleConfirmOrder}
        onCancel={closeOrderModal}
      />

      <PopupModal
        show={popup !== null}
        type={popup?.type || "error"}
        message={popup?.message || ""}
        icon={popup?.icon || ""}
        onClose={() => {
          setPopup(null);
        }}
      />
    </>
  );
};

export default CartPage;
